import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '#environments';
import {
  CpAuditLogEntry,
  CpApprovalRow,
  CpAttachment,
  CpCaseDetail,
  CpCaseListQuery,
  CpCasePage,
  CpMitigationRow,
  CpMoveValidationError,
  CpVerificationRow,
  CreateCpCaseRequest,
  CpCancelCaseRequest,
  SaveCpApprovalRowRequest,
  SaveCpCaseRequest,
  SaveCpMitigationRowRequest,
  SaveCpVerificationRowRequest,
} from '#models';

@Injectable({
  providedIn: 'root',
})
export class CpCasesApiService {
  private _http = inject(HttpClient);
  private readonly _baseUrl = `${environment.apiUrl}/cp-cases`;
  private readonly _lookupUrl = `${environment.apiUrl}/lookup`;

  // ─── Lookups ────────────────────────────────────────────────────────────────

  getLookupOptions(fieldKey: string): Observable<{ value: string }[]> {
    return this._http.get<{ value: string }[]>(`${this._lookupUrl}/${fieldKey}`);
  }

  // ─── Queries ────────────────────────────────────────────────────────────────

  getCases(query?: CpCaseListQuery): Observable<CpCasePage> {
    let params = new HttpParams();
    if (query?.stage) params = params.set('stage', query.stage);
    if (query?.keyword) params = params.set('keyword', query.keyword);
    if (query?.pageNumber != null) params = params.set('pageNumber', query.pageNumber);
    if (query?.pageSize != null) params = params.set('pageSize', query.pageSize);
    return this._http.get<CpCasePage>(this._baseUrl, { params });
  }

  getCase(id: number): Observable<CpCaseDetail> {
    return this._http.get<CpCaseDetail>(`${this._baseUrl}/${id}`);
  }

  getCaseByCaseNumber(caseNumber: string): Observable<CpCaseDetail> {
    return this._http.get<CpCaseDetail>(`${this._baseUrl}/by-number/${encodeURIComponent(caseNumber)}`);
  }

  getAuditLogs(id: number): Observable<CpAuditLogEntry[]> {
    return this._http.get<CpAuditLogEntry[]>(`${this._baseUrl}/${id}/audit`);
  }

  // ─── Case-level mutations ────────────────────────────────────────────────────

  createCase(request: CreateCpCaseRequest): Observable<CpCaseDetail> {
    return this._http.post<CpCaseDetail>(this._baseUrl, request);
  }

  saveCase(id: number, request: SaveCpCaseRequest): Observable<CpCaseDetail> {
    return this._http.put<CpCaseDetail>(`${this._baseUrl}/${id}/save`, request);
  }

  /** Validates mandatory fields and advances CurrentStep. Returns validation errors on failure.
   *  A 400 response body is a CpMoveValidationError[] — treated as a successful (non-throw) result. */
  moveCase(id: number): Observable<CpMoveValidationError[]> {
    return this._http.put<CpMoveValidationError[]>(`${this._baseUrl}/${id}/move`, {}).pipe(
      map(res => res ?? []),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 400 && Array.isArray(err.error)) {
          return of(err.error as CpMoveValidationError[]);
        }
        throw err;
      }),
    );
  }
  /** Saves Initial Reading fields and advances the case to Verification in a single call.
   *  A 400 response body is a CpMoveValidationError[] – treated as a successful (non-throw) result. */
  moveToVerification(id: number, request: SaveCpCaseRequest): Observable<CpMoveValidationError[]> {
    return this._http.put<CpMoveValidationError[]>(`${this._baseUrl}/${id}/verification`, request).pipe(
      map(res => res ?? []),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 400 && Array.isArray(err.error)) {
          return of(err.error as CpMoveValidationError[]);
        }
        throw err;
      }),
    );
  }
  reopenCase(id: number): Observable<CpCaseDetail> {
    return this._http.post<CpCaseDetail>(`${this._baseUrl}/${id}/reopen`, {});
  }

  sendBack(id: number): Observable<CpCaseDetail> {
    return this._http.post<CpCaseDetail>(`${this._baseUrl}/${id}/send-back`, {});
  }

  cancelCase(id: number, dto: CpCancelCaseRequest): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/cancel`, dto);
  }

  // ─── Approval notification ───────────────────────────────────────────────────

  sendApprovalNotification(id: number): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/${id}/notifications/approval`, {});
  }

  sendFeedbackNotification(id: number): Observable<{ sent: boolean }> {
    return this._http.post<{ sent: boolean }>(`${this._baseUrl}/${id}/notifications/feedback`, {});
  }

  // ─── Verification rows ───────────────────────────────────────────────────────

  addVerificationRow(caseId: number, request: SaveCpVerificationRowRequest): Observable<CpVerificationRow> {
    return this._http.post<CpVerificationRow>(`${this._baseUrl}/${caseId}/verification`, request);
  }

  updateVerificationRow(caseId: number, rowId: number, request: SaveCpVerificationRowRequest): Observable<CpVerificationRow> {
    return this._http.put<CpVerificationRow>(`${this._baseUrl}/${caseId}/verification/${rowId}`, request);
  }

  deleteVerificationRow(caseId: number, rowId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${caseId}/verification/${rowId}`);
  }

  // ─── Mitigation rows ─────────────────────────────────────────────────────────

  addMitigationRow(caseId: number, request: SaveCpMitigationRowRequest): Observable<CpMitigationRow> {
    return this._http.post<CpMitigationRow>(`${this._baseUrl}/${caseId}/mitigation`, request);
  }

  updateMitigationRow(caseId: number, rowId: number, request: SaveCpMitigationRowRequest): Observable<CpMitigationRow> {
    return this._http.put<CpMitigationRow>(`${this._baseUrl}/${caseId}/mitigation/${rowId}`, request);
  }

  deleteMitigationRow(caseId: number, rowId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${caseId}/mitigation/${rowId}`);
  }

  // ─── Approval rows ───────────────────────────────────────────────────────────

  addApprovalRow(caseId: number, request: SaveCpApprovalRowRequest): Observable<CpApprovalRow> {
    return this._http.post<CpApprovalRow>(`${this._baseUrl}/${caseId}/approval`, request);
  }

  updateApprovalRow(caseId: number, rowId: number, request: SaveCpApprovalRowRequest): Observable<CpApprovalRow> {
    return this._http.put<CpApprovalRow>(`${this._baseUrl}/${caseId}/approval/${rowId}`, request);
  }

  deleteApprovalRow(caseId: number, rowId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${caseId}/approval/${rowId}`);
  }

  // ─── Attachments ──────────────────────────────────────────────────────────────────

  getAttachments(caseId: number): Observable<CpAttachment[]> {
    return this._http.get<CpAttachment[]>(`${this._baseUrl}/${caseId}/attachments`);
  }

  uploadAttachment(caseId: number, file: File, attachmentType: string): Observable<CpAttachment> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('attachmentType', attachmentType);
    return this._http.post<CpAttachment>(`${this._baseUrl}/${caseId}/attachments`, formData);
  }

  deleteAttachment(caseId: number, attachmentId: number): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${caseId}/attachments/${attachmentId}`);
  }
}
